import React, { useEffect, useState } from 'react';
import { Form, Button, FormProps, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import '../ChangeRequestInput/ChangeRequestInput.less';
import './NewChangeRequestInput.less';
import { createChangeRequest } from '../../../app/ActionTypes/changeRequestActionTypes';
import { useDispatch, useSelector } from 'react-redux';
import { useAgentDispatch } from '../../../hooks/useAgentStore';
import { AppDispatch, RootState } from '../../../app/store';
import { unwrapResult } from '@reduxjs/toolkit';
import MarkdownEditor from '../../MarkdownEditor/MarkdownEditor';
import { Notify } from '../../../services/notificationService';
import {
  getReadonlyElementTitle,
  isUserReadonly,
} from '../../../services/subscription';
import { getAppLoaderController } from '../../../hooks/loadingContext';
import { fetchChangeRequests } from '../../../app/ActionTypes/changeRequestsActions';
import { AppRoutes } from '../../../constants/route';
import ChangeRequestImportButton from '../ChangeRequestImportButton/ChangeRequestImportButton';
import { selectSubscriptionStatus } from '../../../app/Slices/subscriptionSlice';

const NewChangeRequestInput: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const agentDispatch = useAgentDispatch();
  const status = useSelector(selectSubscriptionStatus);
  const org = useSelector((state: RootState) => state.organization.org);
  const [isReadonly, setIsReadonly] = useState(isUserReadonly(status));

  useEffect(() => {
    form.setFieldsValue({
      textInput: {
        markdownText: '',
        isPreviewMode: false,
        showTabs: true,
      },
    });
  }, []);

  useEffect(() => {
    setIsReadonly(isUserReadonly(status));
  }, [org, status]);

  const onSubmit: FormProps<{
    textInput: { markdownText: string };
  }>['onFinish'] = async (values) => {
    const requestText = values.textInput?.markdownText;
    if (requestText.trim() === '') {
      Notify.warning('Please enter a valid Change Request');
      return;
    }
    const { setAppLoading } = getAppLoaderController();
    setAppLoading(true);
    const result = await createNewChangeRequest(requestText);
    setAppLoading(false);
    if (result && result?.changeRequestId) {
      agentDispatch(fetchChangeRequests());
      navigate(`/change-requests/${result?.changeRequestId}`, {
        replace: true,
      });
    }
  };

  const createNewChangeRequest = async (requestText: string) => {
    try {
      const createResponse = await dispatch(
        createChangeRequest({
          summary: requestText,
        })
      );
      const {
        data: { id },
      } = unwrapResult(createResponse);

      if (!id) {
        Notify.warning('Failed to create Change Request!');
        return;
      }

      Notify.success('Change Request created successfully.');
      return { changeRequestId: id };
    } catch (error) {
      return null;
    }
  };

  return (
    <div id="new-change-request-input">
      <div className="new-change-request-wrapper">
        <div className="new-change-request-header">
          <h1>👋 Welcome to {AppRoutes.changeRequests.label}</h1>
          <p>What can I help you implement in your Salesforce org?</p>
        </div>
        <div className="search-bar">
          <Form
            form={form}
            onFinish={onSubmit}
            layout="vertical"
            data-hs-do-not-collect="true"
          >
            <Form.Item
              name="textInput"
              rules={[
                {
                  required: true,
                  message: 'Please input your request details!',
                },
              ]}
            >
              <MarkdownEditor isReadonly={isReadonly} />
            </Form.Item>
            <Form.Item>
              <div className="input-format-helper">
                💡 You can use markdown to format your request.
              </div>
              <div className="action-buttons">
                <div className="button-wrapper">
                  <ChangeRequestImportButton label="Import (JSON)" />
                </div>
                <div className="button-wrapper new-change-request-input-button">
                  <Tooltip title={getReadonlyElementTitle()}>
                    <Button
                      disabled={isReadonly}
                      type="primary"
                      htmlType="submit"
                    >
                      Create Change Request
                    </Button>
                  </Tooltip>
                </div>
              </div>
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default NewChangeRequestInput;
